// ─── src/GewSponsorsView.tsx ─────────────────────────────────────────────────
import { useState } from "react";
import { Download, Trash2, Search, X, Megaphone } from "lucide-react";
import { useGewSponsors, useGewSponsorStats, useDeleteGewSponsor } from "./hooks/useGew";
import { Pagination } from "./components/dashboard/Pagination";
import { TableSkeleton } from "./components/ui/Skeleton";
import { StatCard } from "./components/ui/StatCard";
import type { GewSponsor } from "./api/gew";

const LIMIT = 20;

function exportCsv(rows: GewSponsor[]) {
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = ["Organization", "Email", "Sponsorship Type", "Submitted At"].join(",");
  const body = rows.map((r) =>
    [r.orgName, r.email, r.sponsorshipType ?? "", r.createdAt].map(esc).join(",")
  );
  const blob = new Blob(["﻿" + [header, ...body].join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `gew_sponsors_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function GewSponsorsView() {
  const [search, setSearch] = useState("");
  const [sponsorshipType, setSponsorshipType] = useState("");
  const [page, setPage] = useState(1);

  const { data: stats } = useGewSponsorStats();
  const { data: allData } = useGewSponsors({ page: 1, limit: 2000 });
  const { data: tableData, isLoading, isFetching, isError } =
    useGewSponsors({ page, limit: LIMIT, search, sponsorshipType });

  const { mutate: deleteRow, isPending: isDeleting } = useDeleteGewSponsor();

  const rows = tableData?.data ?? [];
  const meta = tableData?.meta ?? { total: 0, page: 1, limit: LIMIT, totalPages: 1 };
  const typeOptions = Object.keys(stats?.bySponsorshipType ?? {}).sort();

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total inquiries" value={stats?.total ?? 0} icon={Megaphone} accent="bg-brand-orange" />
        {typeOptions.slice(0, 3).map((t) => (
          <StatCard key={t} label={t} value={stats?.bySponsorshipType[t] ?? 0} icon={Megaphone} accent="bg-brand-teal" />
        ))}
      </div>

      <div className="card p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
          <input
            type="text" value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search organization or email…"
            className="w-full pl-9 pr-8 py-2 text-sm bg-brand-navy border border-brand-border rounded-lg text-white placeholder-brand-muted focus:outline-none focus:border-brand-green"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted hover:text-white">
              <X size={14} />
            </button>
          )}
        </div>
        <select
          value={sponsorshipType}
          onChange={(e) => { setSponsorshipType(e.target.value); setPage(1); }}
          className="py-2 px-3 text-sm bg-brand-navy border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green"
        >
          <option value="">All types</option>
          {typeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button
          onClick={() => exportCsv(allData?.data ?? [])}
          disabled={(allData?.data ?? []).length === 0}
          className="btn-primary text-sm flex items-center gap-1.5 ml-auto disabled:opacity-50"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <div className="card p-8 text-center text-red-400">Failed to load sponsors.</div>
      ) : rows.length === 0 ? (
        <div className="card p-10 text-center text-brand-muted">No sponsor inquiries yet.</div>
      ) : (
        <>
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-brand-border text-left text-brand-muted">
                  <th className="px-4 py-3 font-medium">#</th>
                  <th className="px-4 py-3 font-medium">Organization</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Sponsorship Type</th>
                  <th className="px-4 py-3 font-medium">Submitted</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id} className="border-b border-brand-border/50 hover:bg-brand-border/20 transition-colors">
                    <td className="px-4 py-3 text-brand-muted">{(meta.page - 1) * LIMIT + i + 1}</td>
                    <td className="px-4 py-3 text-white" dir="rtl">{r.orgName}</td>
                    <td className="px-4 py-3 text-brand-muted" dir="ltr">{r.email}</td>
                    <td className="px-4 py-3 text-brand-muted" dir="rtl">{r.sponsorshipType ?? "—"}</td>
                    <td className="px-4 py-3 text-brand-muted whitespace-nowrap">
                      {new Date(r.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => deleteRow(r.id)}
                        disabled={isDeleting}
                        className="p-1.5 rounded-lg text-brand-muted hover:text-red-400 hover:bg-red-500/10 disabled:opacity-40"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={meta.page}
            totalPages={meta.totalPages}
            total={meta.total}
            limit={LIMIT}
            onPageChange={setPage}
            isFetching={isFetching}
          />
        </>
      )}
    </div>
  );
}
